import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { AccountService } from '../../../services/account.service';
import { DRAWER_DATA } from 'src/app/shared/injector-tokens';
import { AccountDetailsDrawerData, DrawerService } from 'src/app/services/drawer.service';
import { AccountDetailsComponent } from '../../../accounts/account-details/account-details.component';
import { DrawerLayoutComponent, DrawerLayoutState } from '../../drawer-layout/drawer-layout.component';
import { Account, Credential, CredentialPage } from '../../../types';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-account-details-drawer-wrapper',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './account-details-drawer-wrapper.component.html',
  imports: [DrawerLayoutComponent, AccountDetailsComponent],
})
export class AccountDetailsDrawerWrapperComponent {
  private data = inject<AccountDetailsDrawerData>(DRAWER_DATA);
  private accountService = inject(AccountService);
  private drawerService = inject(DrawerService);

  readonly accountsQuery = injectQuery(() => this.accountService.accountPageQueryOptions());
  readonly ac = computed(() => {
    const accountPage = this.accountsQuery.data();
    const credential = this.credentialByAccountId(accountPage, this.data.accountId);
    const account = credential?.accounts.find(candidate => candidate.id === this.data.accountId);
    return account && credential ? { account, credential } : null;
  });
  readonly logoSrc = computed(() => {
    const credential = this.ac()?.credential;
    return credential ? this.institutionLogoSrc(credential) : null;
  });
  // A settled query without the account (for example, it was removed elsewhere) is an error, not a load.
  readonly layoutState = computed<DrawerLayoutState>(() => {
    if (this.ac()) return 'ready';
    return this.accountsQuery.isPending() ? 'loading' : 'error';
  });

  closeDrawer() {
    this.drawerService.closeDrawer();
  }

  institutionLogoSrc(credential: Credential): string | null {
    const logoUrl = credential.institution.logoUrl;
    if (!logoUrl) return null;
    if (!logoUrl.startsWith('/api/')) return logoUrl;

    return `${environment.restUrl}${logoUrl}`;
  }

  private credentialByAccountId(accountPage: CredentialPage | undefined, accountId: string): Credential | undefined {
    return accountPage?.data.find(credential =>
      credential.accounts.some((account: Account) => account.id === accountId),
    );
  }
}
